import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchJson, postJson, putJson, deleteJson } from '../lib/api';

export interface OrgFilters {
  search?: string;
  type?: string;
  vertical?: string;
  status?: string;
  tag?: string;
  limit?: number;
  offset?: number;
}

export interface Organization {
  id: string;
  name: string;
  type?: string;
  vertical?: string;
  status?: string;
  website?: string;
  phone?: string;
  email?: string;
  contactCount?: number;
  tags?: string[];
  lastActivityAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface OrganizationDetail extends Organization {
  addresses?: { type: string; street: string; city: string; state: string; zip: string }[];
  customFields?: Record<string, string>;
  notes?: string;
  source?: string;
}

export interface OrganizationsResponse {
  organizations: Organization[];
  total: number;
  limit: number;
  offset: number;
}

function buildQueryString(filters?: OrgFilters): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.type) params.set('type', filters.type);
  if (filters.vertical) params.set('vertical', filters.vertical);
  if (filters.status) params.set('status', filters.status);
  if (filters.tag) params.set('tag', filters.tag);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.offset) params.set('offset', String(filters.offset));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useOrganizations(filters?: OrgFilters) {
  return useQuery({
    queryKey: ['organizations', filters],
    queryFn: () =>
      fetchJson<OrganizationsResponse>(`/api/organizations${buildQueryString(filters)}`),
  });
}

export function useOrganization(id: string | null) {
  return useQuery({
    queryKey: ['organization', id],
    queryFn: () => fetchJson<OrganizationDetail>(`/api/organizations/${id}`),
    enabled: !!id,
  });
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<OrganizationDetail>) =>
      postJson<OrganizationDetail>('/api/organizations', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<OrganizationDetail> }) =>
      putJson<OrganizationDetail>(`/api/organizations/${id}`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.invalidateQueries({ queryKey: ['organization', variables.id] });
    },
  });
}

export function useDeleteOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteJson(`/api/organizations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}
